import { fromZonedTime, toZonedTime } from "date-fns-tz";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useDebouncedCallback } from "use-debounce";
import {
  useAllAssetLocationsQuery,
  useOrganizationGeozonesQuery,
  useOrganizationGroupsQuery,
} from "../../api/network/adminApiServices";
import { useLazyGImageryMetaQuery } from "../../api/network/mapApiServices";
import {
  setMapStateData,
  setModalsData,
  setUserMapActivity,
  setUserMapRefreshing,
  setUserMapSelection,
  TMobileHelpers,
  TModalsState,
  TUserMapActivity,
  TUserMapSelection,
} from "../../api/store/commonSlice";
import { LegacyOrganizationAccount } from "../../api/types/Account";
import { OrganizationEntityListingPayload } from "../../api/types/Admin";
import {
  LegacyGeozoneEntityListingPayload,
  LegacyGeozoneType,
  TLegacyGeozone,
} from "../../api/types/Geozone";
import { LegacyGroupEntityListingPayload } from "../../api/types/Group";
import { LegacyVerifyEmailOtpResponseSuccess } from "../../api/types/Onboarding";
import {
  ASSET_MAP_STATUS,
  AssetLocationDetails,
} from "../../api/types/Vehicle";
import svgIcons from "../../assets/svg/svgIcons";
import { mapVehicleIconWrapped } from "../../assets/svg/vehicle-wrapped";
import {
  AdminFormFieldDropdown,
  AdminFormFieldSubmit,
  TSelectboxOption,
} from "../../components/admin/formFields";
import { TGroupSelectorItem } from "../../components/common/groupSelector.type";
import HierarchyGroupSelector from "../../components/common/hierarchyGroupSelector";
import ModalGroupSelector from "../../components/common/modalGroupSelector";
import BasicMap, { MapLoadingAnimation } from "../../components/maps/basicMap";
import MapTopRightControls from "../../components/maps/mapTopRightControls";
import RefreshCountdownBar from "../../components/maps/refreshCountdownBar";
import MapVehicleListingColumnItem from "../../components/mapVehicleListingColumnItem";
import Pagination, { TPaginationSelected } from "../../components/pagination";
import AppSearchBox from "../../components/searchBox";
import SortingFilter from "../../components/sortingFilter";
import TickCheckbox from "../../components/tickCheckbox";
import { APP_CONFIG, GOOGLE_MAP_MODE_NAMES } from "../../constants/constants";
import { useGMapMode } from "../../hooks/mapGoogle";
import { useMapInit } from "../../hooks/mapInit";
import { useAccountTimezone } from "../../hooks/timezone";
import {
  TGeozoneMapDataCircle,
  TGeozoneMapDataPolygon,
  TLatLng,
  TMapLayerOptions,
  TMapState,
  TMapType,
} from "../../types/map";
import { hexToRgb } from "../../utils/common";
import {
  filterOutWhiteGeozones,
  getGeozoneShapeCenter,
} from "../../utils/geozone";
import { useLoggedInUserData } from "../../utils/user";
import { consistentDisplayAssetItem } from "../../utils/vehicle";
import {
  CLUSTER_VEHICLES_VIEW_MORE_EVENT,
  ClusterVehiclesListItem,
  ClusterVehiclesViewMoreEventDetail,
} from "../../utils/map";
import { ACCOUNT_RECORDER_TYPE_VALUES } from "../admin-details/account/constants";
import { mapOperations as mapOperationsCircle } from "../admin-details/geozone/map-circle";
import { mapOperations as mapOperationsPolygon } from "../admin-details/geozone/map-polygon";
import { mapOperations as mapOperationsRoute } from "../admin-details/geozone/map-route";
import { TMapOperationsProps } from "../admin-details/geozone/type";
import {
  geozonePrepareMapState,
  getGeozoneLocs,
} from "../admin-details/geozone/util";
import { useOverviewContext } from "../overview/context/overview.context";
import { getVehicleUUID, mapVehicleMakeModel } from "./common";
import { SORTING_OPTION_VALUES, SORTING_OPTIONS } from "./constants";
import {
  mapOperations,
  mapUpdatesHandler,
  mapVehicleHasValidCoords,
  mapVehicleOrientation,
  mapVehicleStateIconSlug,
  updateEntitiesOnMapTypeChange,
} from "./map";
import ModalLayerFilters from "./modalLayerFilters";
import ModalMapFleetFilters, {
  MapFleetFiltersData,
} from "./ModalMapFleetFilters";
import ModalVehicleDetails from "./modalVehicleDetails";
import ModalClusterVehiclesList from "./modalClusterVehiclesList";
import ModalVehicleLiveCameras from "./modalVehicleLiveCameras";
import {
  TAssetLocationDataPoint,
  TDataPoint,
  TMapData,
  TMapRef,
  TMapRefGeozones,
  TMemoizedMapOperationsProps,
} from "./type";
import DirectionsPanel from "../../components/maps/DirectionPanel";
import { useSingleEnterpriseQuery } from "../../api/network/sysadminApiServices";

// const MAP_UPDATE_DELAY_MS = 60 * 1000;

// main component
const ScreenMapFleet = () => {
  const { deviceId } = useParams<{ deviceId: any }>();
  const { t } = useTranslation("translation", { keyPrefix: "mapFleet" });
  const { t: tAdmin } = useTranslation("translation", {
    keyPrefix: "dashboard.sidemenu.admins",
  });
  const { t: tMain } = useTranslation();
  const dispatch = useDispatch();
  const {
    allGroupsSelected,
    selectedGroups,
    setSelectedGroups,
    setAllGroupsSelected,
  } = useOverviewContext();
  const navigate = useNavigate();
  const thisOrgDriverAllowed = useLoggedInUserData("hasdrivers");
  const showDriver = thisOrgDriverAllowed === "1";
  const thisOrgAssetImageOnMap = useLoggedInUserData("assetimageonmap");
  const isAssetImageEnabled = thisOrgAssetImageOnMap === "1";

  const accountDataInStore = useSelector(
    (state: { commonReducer: { account: LegacyOrganizationAccount } }) =>
      state.commonReducer.account
  );
  const assetSearchFields = useMemo(() => {
    const configuredSearchFields = accountDataInStore?.asset_searchable_fields
      ? accountDataInStore.asset_searchable_fields
          .split(",")
          .map((field: string) => field.trim().toLowerCase())
          .filter(Boolean)
      : APP_CONFIG.ENTITY_SEARCH_FIELDS.DEVICE_LISTING;

    return [
      ...(showDriver
        ? configuredSearchFields
        : configuredSearchFields.filter(
            (field: string) => field !== "drivername"
          )),
      ...APP_CONFIG.ASSET_CONFIGS.MANDATORY_SEARCH_FIELDS,
    ];
  }, [accountDataInStore?.asset_searchable_fields, showDriver]);

  const [showSortingDropdown, setShowSortingDropdown] = useState(false);
  const [selectedSorting, setSelectedSorting] = useState<string>(
    SORTING_OPTION_VALUES.LATEST_FIRST
  );
  const [searchedKeyword, setSearchedKeyword] = useState<string>("");

  const loggedInUserData = useSelector(
    (state: { commonReducer: { user: LegacyVerifyEmailOtpResponseSuccess } }) =>
      state.commonReducer.user
  );
  const mapState = useSelector(
    (state: { commonReducer: { mapState: TMapState } }) =>
      state.commonReducer.mapState
  );
  const userMapActivity = useSelector(
    (state: { commonReducer: { userMapActivity: TUserMapActivity } }) =>
      state.commonReducer.userMapActivity
  );
  const userMapRefreshing = useSelector(
    (state: { commonReducer: { userMapRefreshing: boolean } }) =>
      state.commonReducer.userMapRefreshing
  );
  const [hideMapNearRefresh, setHideMapNearRefresh] = useState(false);
  const userMapSelection = useSelector(
    (state: { commonReducer: { userMapSelection: TUserMapSelection } }) =>
      state.commonReducer.userMapSelection
  );

  const { gMapGetMode, gMapSetMode, gMapEnsureAndLaunchStreetView } =
    useGMapMode();

  const mapWrapperRef = React.createRef<HTMLDivElement>();

  const thisOrgCameraRecorderType = useLoggedInUserData("cameraRecorderType");

  // check if group hierarchy is enabled
  const accountHasGroupHierarchyEnabled = useLoggedInUserData("groupHierarchy");

  const modalsState: TModalsState = useSelector(
    (state: any) => state.commonReducer.modals
  );

  const clearSearchRef = useRef<((preserveZoom?: boolean) => void) | null>(
    null
  );

  const userIsImpersonated = useSelector(
    (state: { commonReducer: { userIsImpersonated: boolean } }) =>
      state.commonReducer.userIsImpersonated
  );

  const mobileHelpers = useSelector(
    (state: { commonReducer: { mobileHelpers: TMobileHelpers } }) =>
      state.commonReducer.mobileHelpers
  );

  const refreshFreqOptions: TSelectboxOption[] = [
    { label: "30 sec", value: "30" },
    { label: "1 min", value: "60" }, // default
    { label: "2 min", value: "120" },
  ];
  const [userRefreshPaused, setUserRefreshPaused] = useState(false); // User-controlled pause state for live updates
  const [refreshFrequency, setRefreshFrequency] = useState(
    refreshFreqOptions[1].value
  ); // "60" // State to control map refresh from Trip details
  const [refreshOptionsExpanded, setRefreshOptionsExpanded] = useState(false); // State to expand/collapse refresh options for map refresh
  // visual progress for refresh countdown (percent: 0-100) — use ref to avoid frequent re-renders
  const refreshProgressRef = useRef<number>(100);
  const refreshTotalMsRef = useRef<number>(
    parseInt(refreshFrequency, 10) * 1000
  );
  // nextRefreshAtRef is the single source of truth for the timestamp (ms) when next poll will happen
  const nextRefreshAtRef = useRef<number | null>(null);
  // requestAnimationFrame id for smooth UI updates
  const rafRef = useRef<number | null>(null);
  const [mapSearchboxOpen, setMapSearchboxOpen] = useState(false);
  const [clusterVehiclesModal, setClusterVehiclesModal] = useState<{
    visible: boolean;
    count: number;
    entityLabel: string;
    markers: ClusterVehiclesListItem[];
  }>({
    visible: false,
    count: 0,
    entityLabel: "assets",
    markers: [],
  });
  const refreshPaused =
    userRefreshPaused || mapSearchboxOpen || clusterVehiclesModal.visible;

  // when refresh frequency changes, update total and schedule next refresh from now
  useEffect(() => {
    const total = parseInt(refreshFrequency, 10) * 1000;
    refreshTotalMsRef.current = total;
    nextRefreshAtRef.current = Date.now() + total;
    refreshProgressRef.current = 100;
  }, [refreshFrequency]);

  // Render progress bar based on nextRefreshAtRef using requestAnimationFrame so the visual
  // countdown is always aligned with the polling schedule (single source of truth).
  useEffect(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    if (refreshPaused) return () => {};

    if (!nextRefreshAtRef.current) {
      nextRefreshAtRef.current = Date.now() + refreshTotalMsRef.current;
      refreshProgressRef.current = 100;
    }

    const loop = () => {
      const nextAt = nextRefreshAtRef.current;
      if (!nextAt) return;
      const remaining = Math.max(0, nextAt - Date.now());
      const pct = (remaining / refreshTotalMsRef.current) * 100;
      refreshProgressRef.current = Math.max(0, Math.round(pct));
      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [refreshPaused, refreshFrequency]);

  // If another part of the app triggers a refresh, align our next-refresh timestamp
  useEffect(() => {
    if (userMapRefreshing) {
      nextRefreshAtRef.current = Date.now() + refreshTotalMsRef.current;
      refreshProgressRef.current = 100;
    }
  }, [userMapRefreshing]);

  const [selectedVehicle, setSelectedVehicle] = useState<string | null>();
  const [checkedVehicles, setCheckedVehicles] = useState<string[]>([]);
  const [allRetrievedVehicles, setAllRetrievedVehicles] =
    useState<AssetLocationDetails[]>();
  const vehiclesBatch = useRef(1); // to keep track of batched fetching of vehicles
  const [vehicleListCurrentPage, setVehicleListCurrentPage] = useState(1); // to keep track of current page of vehicle listing (frontend pagination)
  const [directionsRenderer, setDirectionsRenderer] = React.useState(null);

  const handleDirectionsChange = (renderer: any) => {
    setDirectionsRenderer(renderer);
  };

  const closeClusterVehiclesModal = useCallback(() => {
    setClusterVehiclesModal((prev) => ({ ...prev, visible: false }));
  }, []);

  const dataPointsRef = useRef<TAssetLocationDataPoint[]>([]);

  const handleClusterVehicleDetailsOpen = useCallback(
    (deviceId: string) => {
      closeClusterVehiclesModal();
      const matchedDataPoint = dataPointsRef.current.find(
        (dp) => dp.deviceid === deviceId
      );
      if (matchedDataPoint) {
        const uuid = getVehicleUUID(matchedDataPoint);
        selectMapVehicle(uuid);
      }
    },
    [closeClusterVehiclesModal] // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    const handleClusterViewMore = (event: Event) => {
      const customEvent =
        event as CustomEvent<ClusterVehiclesViewMoreEventDetail>;
      if (!customEvent?.detail) return;

      setClusterVehiclesModal({
        visible: true,
        count: customEvent.detail.count,
        entityLabel: customEvent.detail.entityLabel,
        markers: customEvent.detail.markers || [],
      });
    };

    window.addEventListener(
      CLUSTER_VEHICLES_VIEW_MORE_EVENT,
      handleClusterViewMore as EventListener
    );

    return () => {
      window.removeEventListener(
        CLUSTER_VEHICLES_VIEW_MORE_EVENT,
        handleClusterViewMore as EventListener
      );
    };
  }, []);

  // map init if not already done
  const { initMap, gIdentifyAndSetMapModeToStore } = useMapInit();
  useEffect(() => {
    if (!mapState?.mapScriptLoaded) {
      initMap();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * reset map type on mount
   * -----------------------
   * Before Sep 23 2024, we were trying to set map type to road on unmount,
   * but it was causing issues with mapdata as rest of the map data
   * including mapScriptLoaded was getting erased.
   * This was happening because `mapState` (store data) was unavailable on unmount.
   */
  useEffect(() => {
    dispatch(
      setMapStateData({
        // ...mapState,
        mapType: APP_CONFIG.MAPS.TYPES[APP_CONFIG.MAPS.TYPES_INDEX.ROADMAP]
          .type as TMapType,
      })
    );
    return () => {
      if (APP_CONFIG.DEBUG.MAPS) {
        console.log("[Map-Fleet] Resetting map type to ROADMAP on unmount");
      }
      // reset everything
      resetPaginationForBatchFetching();
      dispatch(
        setMapStateData({
          // ...mapState,
          mapType: APP_CONFIG.MAPS.TYPES[APP_CONFIG.MAPS.TYPES_INDEX.ROADMAP]
            .type as TMapType,
        })
      );
      dispatch(
        setUserMapActivity({
          ...userMapActivity,
          assetMap: {
            center: null,
            zoom: APP_CONFIG.MAPS.DEFAULTS.ZOOM,
            mapType: null,
          },
          fleetMap: {
            center: null,
            zoom: APP_CONFIG.MAPS.DEFAULTS.ZOOM,
            mapType: null,
          },
        })
      );
      dispatch(setUserMapRefreshing(false));
      dispatch(
        setUserMapSelection({
          ...userMapSelection,
          assetMap: {
            selectedTrip: null,
            selectedTripPoint: null,
          },
          fleetMap: {
            selectedAssetItem: null,
          },
        })
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // implemented to revert back to last zoomed view when vehicle details modal is closed
  // however, this behaviour is disabled as of 7/2/2024 as it is clashing with other map operations
  const [lastMapView, setLastMapView] = useState<any>(null);


  // handle vehicle selection
  const selectMapVehicle = useCallback(
    (vehicleId: string | null) => {
      // re-center map...
      if (vehicleId === null) {
        // disabled 7/2/2024, nothing happens when vehicle details modal is closed
        // if(!!lastMapView && !!lastMapView.center && !!lastMapView.zoom) {
        //   // revert to last zoomed view
        //   mapUpdatesHandler(
        //     getMapOpsProps(),
        //     'centerToCoords',
        //     {
        //       center: [
        //         lastMapView.center.latitude,
        //         lastMapView.center.longitude,
        //       ],
        //       zoom: lastMapView.zoom,
        //     }
        //   );
        // } else {
        // Back to the view the asset was focused from. This used to frame
        // every checked vehicle instead - the whole fleet, 800ms later -
        // which is not where you were and, with nothing checked, quietly
        // did nothing at all and left the map at ZOOM_MAX over the pin.
        mapUpdatesHandler(
          getMapOpsProps(),
          "restorePreFocusView",
          checkedVehicles
        );
        // }
      }
      // center map to selected vehicle and update the vehicle icon to focussed state
      setSelectedVehicle((prevVehicleId) => {
        const previousVehicleId = prevVehicleId ?? selectedVehicle;
        if (!!previousVehicleId && previousVehicleId !== vehicleId) {
          // Only a *different* pin gives up its highlight and its ring. This
          // ran for every selection before, the already-selected asset
          // included, so clicking that asset again stopped its pulse.
          mapUpdatesHandler(getMapOpsProps(), "focusPushpin", {
            id: previousVehicleId,
            focus: false,
          });
        } else if (!!vehicleId && previousVehicleId === vehicleId) {
          // The same asset, clicked again. Nothing about the selection
          // changes, so returning it below is a no-op React does not
          // re-render for and the effect that focuses the pin will not run -
          // assert it here instead: re-centre on the asset, keep the pin
          // highlighted, keep the ring up.
          mapUpdatesHandler(getMapOpsProps(), "focusPushpin", {
            id: vehicleId,
            focus: true,
          });
        }
        // if (prevVehicleId === vehicleId) {
        //   setTimeout(() => {
        //     dispatch(
        //       setModalsData({
        //         ...modalsState,
        //         showGroupSelector: false,
        //         showHierarchyGroupSelector: false,
        //         showVehicleDetails: false,
        //       })
        //     );
        //   }, 200); // delay prevents a `Cannot update a component` warning
        //   console.log("RETRUNING NULL");
        //   return null;
        // }
        console.log("RETURNING VEHICLE ID:", vehicleId);
        return vehicleId;
      });
    },
    [checkedVehicles, selectedVehicle, modalsState] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // update map view on vehicle selection
  useEffect(() => {
    if (!!selectedVehicle) {
      dispatch(
        setModalsData({
          ...modalsState,
          showLayerFilter: false,
          showVehicleFilter: false,
          showVehicleDetails: true,
        })
      );
      // center map to selected vehicle only if it has valid coordinates
      const vehicle = dataPoints.find(
        (item) => getVehicleUUID(item) === selectedVehicle
      );
      if (mapVehicleHasValidCoords(vehicle)) {
        mapUpdatesHandler(getMapOpsProps(), "focusPushpin", {
          id: selectedVehicle,
          focus: true,
        });
      }
    }
    // update store with selected vehicle
    dispatch(
      setUserMapSelection({
        ...userMapSelection,
        fleetMap: {
          selectedAssetItem: !!selectedVehicle ? selectedVehicle : null,
        },
      })
    );
  }, [selectedVehicle]);

  // reset selectedVehicle state on modal close
  useEffect(() => {
    if (!modalsState.showVehicleDetails) {
      console.log("calling selectMapVehicle with value: (A)", null);
      selectMapVehicle(null);
    }
  }, [modalsState.showVehicleDetails]);

  // preparing query params
  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");
  const [orgVehiclesQueryParams, setOrgVehiclesQueryParams] =
    React.useState<OrganizationEntityListingPayload>({
      accountid: `${thisUserOrganizationId}`,
      page: 1,
      page_size: APP_CONFIG.LISTINGS.VEHICLE_PAGE_SIZE,
      search: "",
      //is_active: "both", // TODO: [P] need to discuss with hemant not in API params
    });
  const debouncedSetSearchKeyword = useDebouncedCallback((value: string) => {
    // resetPaginationForBatchFetching();
    // setOrgVehiclesQueryParams((prev) => { return { ...prev, page: 1, search: value } });
    setSearchedKeyword(value);
    console.log("calling selectMapVehicle with value: (B)", null);
    selectMapVehicle(null);
    setVehicleListCurrentPage(1); // reset pagination
  }, 500);

  // fetch groups data
  const {
    data: dataOrgGroups,
    isFetching: isFetchingOrgGroups,
    error: errorOrgGroups,
  } = useOrganizationGroupsQuery(
    {
      accountid: `${thisUserOrganizationId}`,
      page: 1,
      page_size: APP_CONFIG.LISTINGS.XL_PAGE_SIZE,
      search: "",
    } as LegacyGroupEntityListingPayload,
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const [filteredGroupData, setFilteredGroupData] = useState<
    TGroupSelectorItem[]
  >([]);

  useEffect(() => {
    setFilteredGroupData(
      (accountHasGroupHierarchyEnabled === "1"
        ? []
        : (dataOrgGroups?.results?.map((group) => {
            return {
              groupid: group.groupid,
              description: group.description,
              is_hgroup: false,
              checked: true,
            };
          }) ?? [])) as TGroupSelectorItem[]
    );
  }, [dataOrgGroups]);

  // event data filters
  const { tzForDateFns, tzForDisplay, convertToTZForDateFns } =
    useAccountTimezone();
  const [dateFiltersUpdated, setDateFiltersUpdated] = useState<boolean>(false);
  const [eventDataFilters, setEventDataFilters] = useState<MapFleetFiltersData>(
    {
      date_time: toZonedTime(
        new Date(),
        convertToTZForDateFns(tzForDateFns)
      ).toISOString(),
      timezone: tzForDisplay,
      calculatedUnixTimestamp: toZonedTime(
        new Date(),
        convertToTZForDateFns(tzForDateFns)
      ).getTime(),
    }
  );

  // reset last user activity when the date filter changes to allow
  // the map to focus (fitBounds) on the new set of markers after date change
  useEffect(() => {
    dispatch(
      setUserMapActivity({
        ...userMapActivity,
        assetMap: {
          center: null,
          zoom: APP_CONFIG.MAPS.DEFAULTS.ZOOM,
          mapType: null,
        },
        fleetMap: {
          center: null,
          zoom: APP_CONFIG.MAPS.DEFAULTS.ZOOM,
          mapType: null,
        },
      })
    );
  }, [eventDataFilters]);

  // fetch vehicles data with new single API call (with filters)
  const {
    data: dataAllAssetLocations,
    isFetching: isFetchingAllAssetLocations,
    refetch: refetchAllAssetLocations,
  } = useAllAssetLocationsQuery(
    {
      ...(!!dateFiltersUpdated && !!eventDataFilters.calculatedUnixTimestamp // apply timestamp filter only if user has changed date filters
        ? { timestamp: eventDataFilters.calculatedUnixTimestamp }
        : {}),
      // ...(filteredGroupData.filter((group) => group.checked).length !==
      //   dataOrgGroups?.results?.length &&
      // filteredGroupData.filter((group) => group.checked).length > 0
      //   ? {
      //       group_ids: filteredGroupData
      //         .filter((group) => group.checked)
      //         .map((group) => group.groupid)
      //         .join(","),
      //     }
      //   : {}),
      // 01/28/2025 commented code above code and added the below code to fix the issue of API not triggering on group selection change
      ...(selectedGroups.length > 0 && !allGroupsSelected
        ? {
            group_ids: selectedGroups.map((group: string) => group).join(","),
          }
        : {}),
    },
    {
      skip: !mapState?.mapScriptLoaded,
      refetchOnMountOrArgChange: true,
      // pollingInterval: window.location.hostname.includes("localhost")
      //   ? undefined
      //   : MAP_UPDATE_DELAY_MS,
      // polling disabled, will use manual refetching based on user refresh fequency preference
    }
  );

  const loggedInAccountId = useLoggedInUserData("ownerOrganizationId");

  const { data: accountSettings } = useSingleEnterpriseQuery(
    { enterprise_id: loggedInAccountId || "" },
    {
      skip: !loggedInAccountId,
      refetchOnFocus: true,
      refetchOnMountOrArgChange: true,
    }
  );

  // manual polling based on user refresh frequency preference
  useEffect(() => {
    const total = parseInt(refreshFrequency, 10) * 1000;
    // schedule next refresh from now
    nextRefreshAtRef.current = Date.now() + total;
    refreshProgressRef.current = 100;

    const pollingInterval = setInterval(() => {
      if (!refreshPaused) {
        if (APP_CONFIG.DEBUG.ALL) {
          console.log(
            `[Map-Fleet] Refetching all asset locations as per user preference - every ${refreshFrequency} seconds`
          );
        }
        setHideMapNearRefresh(true);
        dispatch(setUserMapRefreshing(true)); // this ensures that map activity is not recorded while refreshing
        refetchAllAssetLocations();
        // after triggering refresh, schedule next refresh from now so UI aligns with actual poll
        nextRefreshAtRef.current = Date.now() + total;
        refreshProgressRef.current = 100;
      }
    }, total);

    return () => {
      clearInterval(pollingInterval);
    };
  }, [refreshPaused, refreshFrequency, refetchAllAssetLocations]);

  // update allRetrievedVehicles with fetched data
  useEffect(() => {
    // reset and close popups if polling is in progress
    if (isFetchingAllAssetLocations) {
      setAllRetrievedVehicles([]); // resetting to [] ensures the left column also re-renders with updates
      dispatch(
        setModalsData({
          ...modalsState,
          // showGroupSelector: false, // disabling this to prevent closing of groups selector popup on every refresh
          showHierarchyGroupSelector: false,
          // showVehicleDetails: false, // disabling this to prevent closing of vehicle details modal on every refresh
          showLayerFilter: false,
          // showVehicleFilter: false, // disabling this to prevent closing of date filter popup on every refresh
        })
      );
    }
    // if fresh data is available, make necessary updates
    if (!!dataAllAssetLocations && !isFetchingAllAssetLocations) {
      setAllRetrievedVehicles(dataAllAssetLocations);
      setTimeout(() => {
        setHideMapNearRefresh(false);
      }, 2 * 1000); // smaller delay to show a loader near refreshes
      setTimeout(() => {
        dispatch(setUserMapRefreshing(false));
      }, 5 * 1000); // small delay to ensure map updates are done before re-enabling user activity recording
    }
  }, [dataAllAssetLocations, isFetchingAllAssetLocations]);

  // const {
  //   data: dataVehiclesBatch,
  //   isFetching: isFetchingVehiclesBatch,
  //   error
  // } =
  //   useOrganizationVehiclesQuery(
  //     {
  //       ...orgVehiclesQueryParams,
  //       ordering: selectedSorting,
  //       ...(
  //         filteredGroupData.filter(group => group.checked).length !== dataOrgGroups?.results?.length
  //           && filteredGroupData.filter(group => group.checked).length > 0
  //           ? {
  //             group_ids: filteredGroupData
  //               .filter(group => group.checked)
  //               .map(group => group.groupid)
  //               .join(","),
  //           }
  //           : {}
  //       )
  //       // group_ids: (dataOrgGxroups?.results?.map((item) => item.groupid) ?? [])?.join(","),
  //     },
  //     {
  //       skip: !mapState?.mapScriptLoaded || !dataOrgGroups || isFetchingOrgGroups,
  //       refetchOnMountOrArgChange: true,
  //     }
  //   );

  // fetch vehicles in batches sequentially and update allRetrievedVehicles
  // const { count, next, previous, results } = dataVehiclesBatch || {};
  // useEffect(() => {
  //   if (APP_CONFIG.DEBUG.MAPS) console.log('next useEffect')
  //   if (next) {
  //     if (APP_CONFIG.DEBUG.MAPS) console.log('next')
  //     if (vehiclesBatch.current === 1) {
  //       setAllRetrievedVehicles(dataVehiclesBatch);
  //     } else {
  //       setAllRetrievedVehicles({
  //         count: count || 1,
  //         next: next || "",
  //         previous: previous || "",
  //         results: allRetrievedVehicles?.results?.concat(results ?? []) || [],
  //       });
  //     }
  //     vehiclesBatch.current = vehiclesBatch.current + 1;
  //     setOrgVehiclesQueryParams({ ...orgVehiclesQueryParams, page: vehiclesBatch.current });
  //   } else {
  //     if (APP_CONFIG.DEBUG.MAPS) console.log('no next', 'results:', results, 'allRetrievedVehicles:', allRetrievedVehicles)
  //     setAllRetrievedVehicles(
  //       !!allRetrievedVehicles
  //         ? {
  //           count: count || 1,
  //           next: next || "",
  //           previous: previous || "",
  //           results:
  //             allRetrievedVehicles?.results?.concat(
  //               results?.filter((item) => !allRetrievedVehicles?.results?.find((i) => i.deviceid === item.deviceid)) // ensure no duplicate vehicles
  //               ?? []
  //             ) || [],
  //         }
  //         : dataVehiclesBatch
  //     );
  //   }
  // }, [next])

  const resetPaginationForBatchFetching = () => {
    setVehicleListCurrentPage(1);
    setAllRetrievedVehicles(undefined);
    vehiclesBatch.current = 1;
    setOrgVehiclesQueryParams({ ...orgVehiclesQueryParams, page: 1 });
    console.log("calling selectMapVehicle with value: (C)", null);
    selectMapVehicle(null);
  };

  const handleGroupFilterChange = (groups: TGroupSelectorItem[]) => {
    resetPaginationForBatchFetching();
    setFilteredGroupData(groups);
  };

  // USE OF (GLOBAL) VEHICLE EVENTS API DEPRECATED, INSTEAD WE HAVE ADAPTED TO NEWER API SET VIA `useAllAssetLocationsQuery`
  // const {
  //   data: allRetrievedVehiclesEvents,
  //   isFetching: isFetchingOrgVehiclesEvents,
  // } = useOrganizationVehiclesEventsQuery(
  //   {
  //     account_id: `${thisUserOrganizationId}`,
  //     ...(!!eventDataFilters.calculatedUnixTimestamp ? { timestamp: eventDataFilters.calculatedUnixTimestamp } : {})
  //   },
  //   {
  //     skip: !mapState?.mapScriptLoaded || !dataOrgGroups || isFetchingOrgGroups || !allRetrievedVehicles || isFetchingAllAssetLocations || allRetrievedVehicles?.length === 0,
  //     refetchOnMountOrArgChange: true,
  //   }
  // );

  const [dataPoints, setDataPoints] = useState<TAssetLocationDataPoint[]>([]);
  // keep ref in sync so callbacks declared before this state can access current value
  useEffect(() => {
    dataPointsRef.current = dataPoints;
  }, [dataPoints]);

  // helper function to get map operations props
  const getMapOpsProps: TMemoizedMapOperationsProps = useCallback(() => {
    return {
      mapRef,
      mapState,
      mapData: { ...mapState?.mapData } as TMapData,
      mapLayerOptions: mapState?.mapLayerOptions as TMapLayerOptions,
      setMapData: () => {},
      dataPoints,
      isAssetImageEnabled,
      accountSettings: accountSettings
        ? {
            infoboxassetid: accountSettings.infoboxassetid ?? 0,
            infoboxassetdesc: accountSettings.infoboxassetdesc ?? 0,
            infoboxassetdisplay: accountSettings.infoboxassetdisplay ?? 0,
            infoboxdriver: accountSettings.infoboxdriver ?? 0,
            infoboxalwaysopen: accountSettings.infoboxalwaysopen ?? 0,
          }
        : undefined,
      onViewChangeEnd: (center, zoom) => {
        if (APP_CONFIG.DEBUG.MAPS)
          console.log("[onViewChangeEnd] center:", center, "zoom:", zoom);
        dispatch(
          setMapStateData({
            // ...mapState,
            mapCenter: center,
            mapZoom: zoom,
            mapData: {
              ...mapState.mapData,
              centerPosition: center,
            },
            mapType:
              gMapGetMode(mapRef.current.map) ===
              GOOGLE_MAP_MODE_NAMES.STREETVIEW
                ? (APP_CONFIG.MAPS.TYPES[APP_CONFIG.MAPS.TYPES_INDEX.ROADMAP]
                    .type as TMapType)
                : mapRef.current.map?.getMapTypeId() ===
                    APP_CONFIG.MAPS.TYPES[APP_CONFIG.MAPS.TYPES_INDEX.SATELLITE]
                      .id
                  ? (APP_CONFIG.MAPS.TYPES[
                      APP_CONFIG.MAPS.TYPES_INDEX.SATELLITE
                    ].type as TMapType)
                  : mapState.mapType,
          })
        );
        // this function gets triggered on closing streetside view
        // make map entities visible here
        if (
          mapState?.mapType ===
            APP_CONFIG.MAPS.TYPES[APP_CONFIG.MAPS.TYPES_INDEX.ROADMAP].type &&
          mapRef.current.map?.getMapTypeId() ===
            APP_CONFIG.MAPS.TYPES[APP_CONFIG.MAPS.TYPES_INDEX.ROADMAP].id
        ) {
          if (APP_CONFIG.DEBUG.MAPS)
            console.log(
              `Attempt to switch to road view (when closing streetside), params: ${mapState?.mapType}`
            );
        }
        // if(APP_CONFIG.DEBUG.MAPS) console.log('Switching to road view after streetside view is closed')
        // handleMapTypeChange('road')
      },
      onMapTypeChange: (event) => {
        if (APP_CONFIG.DEBUG.MAPS) console.log("[onMapTypeChange]", event);
        // with google maps, store value updates automatically via `tilesloaded` event, so
        // we just update the map mode using Google Maps JS SDK using `gMapSetMode` in
        // `handleMapTypeChange` below
        // -------------------------------------------------------------------------------
        // dispatch(
        //   setMapStateData({
        //     ...mapState,
        //     mapType:
        //       (APP_CONFIG.MAPS.TYPES.find(
        //         (item) => item.id === event.newMapTypeId
        //       )?.type as TMapType) ?? "",
        //   })
        // );
      },
      // if this is a refresh, send override param
      ...(!!userMapActivity?.fleetMap?.center &&
      !!userMapActivity?.fleetMap?.zoom
        ? {
            overrideMapView: {
              center: userMapActivity?.fleetMap?.center || null,
              zoom:
                userMapActivity?.fleetMap?.zoom ||
                APP_CONFIG.MAPS.DEFAULTS.ZOOM,
              mapType: userMapActivity?.fleetMap?.mapType || null,
            },
          }
        : {}),
      saveLastView: (center: any, zoom: number) => {
        if (APP_CONFIG.DEBUG.MAPS) console.log("[saveLastView]", center, zoom);
        setLastMapView({ center, zoom });
      },
    };
  }, [mapState, dataPoints, userMapActivity, accountSettings]);

  // show address searchbox
  const [showAddressSearchbox, _] = useState(false);

  // common map ref to be used for various map operations
  const mapRef = React.useRef<TMapRef>({
    map: null,
    objects: {
      mPushpins: [],
      mClusterLayer: null,
    },
  });

  
  useEffect(() => {
    const ref = mapRef;
    return () => {
      ref.current.selectedPulse?.destroy();
      ref.current.selectedPulse = undefined;
    };
  }, []);

  // fetch geozones data
  const { data: dataOrgGeozones, isFetching: isFetchingOrgGeozones } =
    useOrganizationGeozonesQuery(
      {
        accountid: `${thisUserOrganizationId}`,
        page_size: APP_CONFIG.LISTINGS.XL_PAGE_SIZE,
        page: 1,
      } as LegacyGeozoneEntityListingPayload,
      {
        skip: !mapState?.mapScriptLoaded,
      } // || mapState?.mapLayerOptions?.hide_geozones
    );

  // common map ref for geozone map operations
  const mapRefGeozones = React.useRef<TMapRefGeozones>({});

  const renderGeozones = () => {
    if (mapState?.mapLayerOptions?.hide_geozones) return;
    if (
      !!dataOrgGeozones &&
      !isFetchingOrgGeozones &&
      !!mapState?.mapScriptLoaded // mapState?.mapData?.ready
    ) {
      if (APP_CONFIG.DEBUG.MAPS)
        console.log("[handleMapReady] [renderGeozones]");
      dataOrgGeozones?.results
        ?.filter(filterOutWhiteGeozones)
        ?.forEach((geozone) => {
          // initialize map ref for each geozone
          const thisGeozoneMapRef = (mapRefGeozones.current[geozone.geozoneid] =
            {
              current: {
                map: mapRef.current.map,
                objects: {},
              },
            });
          // prepare map state for each geozone
          const geozoneLocs = getGeozoneLocs(
            geozone as Partial<TLegacyGeozone>
          );
          const geozoneCenter = getGeozoneShapeCenter(
            geozone as Partial<TLegacyGeozone>,
            "object"
          ) as TLatLng;
          const mapDataFromSavedRecord = {
            //values.properties as TGeozoneMapData;
            centerPosition: geozoneCenter,
            locs: geozoneLocs,
            radius: geozone.radius,
          };
          const newMapState: TMapState = geozonePrepareMapState({
            seedMapState: mapState,
            shapeType: geozone.zonetype,
            seedMapData: mapDataFromSavedRecord,
            centerPosition:
              mapDataFromSavedRecord?.centerPosition ??
              APP_CONFIG.MAPS.DEFAULTS.CENTER,
            polygonSides:
              (mapDataFromSavedRecord as TGeozoneMapDataPolygon)?.locs
                ?.length ?? APP_CONFIG.MAPS.DEFAULTS.POLYGON_POINTS,
            locs:
              (mapDataFromSavedRecord as TGeozoneMapDataPolygon)?.locs ??
              undefined,
            radius:
              (mapDataFromSavedRecord as TGeozoneMapDataCircle)?.radius ??
              APP_CONFIG.MAPS.DEFAULTS.RADIUS,
            color: hexToRgb(`${geozone.shapecolor}`), // APP_CONFIG.MAPS.COLORS[values.shapecolor as keyof typeof APP_CONFIG.MAPS.COLORS],
            editable: false,
            ready: true,
          });
          const mapOperationsParams = {
            mapRef: thisGeozoneMapRef, // mapRefGeozones.current[geozone.geozoneid],
            mapData: newMapState.mapData || mapDataFromSavedRecord,
            setMapData: () => {},
            drawShapeOnly: true,
            geozoneId:
              loggedInUserData?.user?.enablegeozonelabels === 1
                ? geozone.geozoneid
                : undefined,
          } as TMapOperationsProps;
          // check zone type and call respective map operations
          if (geozone.zonetype === null || isNaN(geozone.zonetype)) return; // prevents error when zonetype is null due to bad API data
          switch (parseInt(geozone?.zonetype?.toString())) {
            case LegacyGeozoneType.CIRCLE:
              if (APP_CONFIG.DEBUG.GEOZONES)
                console.log("geozoneid:", geozone.geozoneid, "[CIRCLE]");
              mapOperationsCircle(mapOperationsParams);
              break;
            case LegacyGeozoneType.POLYGON:
              if (APP_CONFIG.DEBUG.GEOZONES)
                console.log("geozoneid:", geozone.geozoneid, "[POLYGON]");
              mapOperationsPolygon(mapOperationsParams);
              break;
            case LegacyGeozoneType.ROUTE:
              if (APP_CONFIG.DEBUG.GEOZONES)
                console.log("geozoneid:", geozone.geozoneid, "[ROUTE]");
              mapOperationsRoute(mapOperationsParams);
              break;
            default:
              break;
          }
        });
    } else {
      if (APP_CONFIG.DEBUG.MAPS)
        console.log(
          "[handleMapReady] [renderGeozones] ELSE ",
          !!dataOrgGeozones,
          !isFetchingOrgGeozones,
          mapState?.mapData?.ready
        );
    }
  };

  // used for any map state transition to refresh map entities
  const [mapStateTransitionInProgress, setMapStateTransitionInProgress] =
    useState(false);

  const [showLegendPopup, setShowLegendPopup] = useState(false);
  // the legend doubles as a status filter. Collapsed state is its own, so
  // someone working in a crowded corner of the map can fold it away
  // without losing whatever they have filtered to.
  const [legendCollapsed, setLegendCollapsed] = useState(false);
  // which statuses are on show. Everything by default - an empty set would
  // mean an empty map on first load, which reads as broken rather than as
  // a filter nobody has set yet.
  const ALL_MAP_STATUSES = [
    ASSET_MAP_STATUS.DRIVING,
    ASSET_MAP_STATUS.IDLE,
    ASSET_MAP_STATUS.IGNITION_OFF,
    ASSET_MAP_STATUS.BATTERY_POWERED,
    ASSET_MAP_STATUS.NOT_REPORTING,
  ];
  const [statusFilter, setStatusFilter] = useState<ASSET_MAP_STATUS[]>(
    ALL_MAP_STATUSES
  );
  const statusFilterIsActive = statusFilter.length < ALL_MAP_STATUSES.length;
  const toggleStatus = (status: ASSET_MAP_STATUS) =>
    setStatusFilter((previous) =>
      previous.includes(status)
        ? previous.filter((item) => item !== status)
        : [...previous, status]
    );
  // This narrows what the group filter already chose - it never widens it.
  // The group selection is applied server side (group_ids on the query),
  // so everything here is working within the groups on screen.
  const matchesStatusFilter = (dataPoint: TAssetLocationDataPoint) =>
    statusFilter.includes(dataPoint.asset_map_status);

  const legendItems = [
    {
      status: ASSET_MAP_STATUS.DRIVING,
      label: t("vehicleStatus.driving"),
      description: "Ignition ON, speed > 0",
      colorClass: "text-field-success",
      iconHtml: mapVehicleIconWrapped("driving", false, 45),
    },

    {
      status: ASSET_MAP_STATUS.IDLE,
      label: t("vehicleStatus.idle_active"),
      description: "Ignition ON, speed = 0",
      colorClass: "text-field-success",
      iconHtml: mapVehicleIconWrapped(
        mapVehicleStateIconSlug({
          asset_map_status: ASSET_MAP_STATUS.IDLE,
        } as any),
        false,
        45
      ),
    },
    {
      status: ASSET_MAP_STATUS.IGNITION_OFF,
      label: t("vehicleStatus.ignition_off"),
      description: "Ignition OFF or Trip End",
      colorClass: "text-accent-orange",
      iconHtml: mapVehicleIconWrapped(
        mapVehicleStateIconSlug({
          asset_map_status: ASSET_MAP_STATUS.IGNITION_OFF,
        } as any),
        false,
        45
      ),
    },
    {
      status: ASSET_MAP_STATUS.BATTERY_POWERED,
      label: t("vehicleStatus.battery_powered"),
      description: "Battery unit, reports a few times a day",
      colorClass: "text-[#2A6FDB]",
      iconHtml: mapVehicleIconWrapped(
        mapVehicleStateIconSlug({
          asset_map_status: ASSET_MAP_STATUS.BATTERY_POWERED,
        } as any),
        false,
        45
      ),
    },
    {
      status: ASSET_MAP_STATUS.NOT_REPORTING,
      label: t("vehicleStatus.idle_inactive"),
      description: "No data for 27+ hours",
      colorClass: "text-field-error-dark",
      iconHtml: mapVehicleIconWrapped(
        mapVehicleStateIconSlug({
          asset_map_status: ASSET_MAP_STATUS.NOT_REPORTING,
        } as any),
        false,
        45
      ),
    },
  ];

  // Each row is both the key to what an icon means and the switch for
  // whether that status is on the map - the two were always describing the
  // same four things, so one control does both.
  const MemoizedLegendBox = useMemo(() => {
    return (
      <div className="flex flex-col gap-1">
        {legendItems.map((item) => {
          const isOn = statusFilter.includes(item.status);
          const count = dataPoints.filter(
            (dataPoint) => dataPoint.asset_map_status === item.status
          ).length;
          return (
            <button
              type="button"
              key={item.label}
              onClick={() => toggleStatus(item.status)}
              aria-pressed={isOn}
              title={
                isOn
                  ? `Hide ${item.label} assets`
                  : `Show ${item.label} assets`
              }
              className={`w-full text-left flex items-center justify-between py-1 px-2 rounded-md gap-2 cursor-pointer hover:bg-accent-blue-paleO66 ${
                isOn ? "" : "opacity-40"
              }`}
            >
              <input
                type="checkbox"
                readOnly
                checked={isOn}
                tabIndex={-1}
                className="size-3.5 accent-accent-blue-dark shrink-0 pointer-events-none"
              />
              <div className="flex flex-col flex-grow min-w-0">
                <span
                  className={`text-sm font-medium leading-4 ${item.colorClass}`}
                >
                  {item.label}
                  <span className="text-gray-400 font-normal"> ({count})</span>
                </span>
                <span className="text-[10px] text-gray-400 leading-3 mt-0.5">
                  {item.description}
                </span>
              </div>
              <span
                className="flex size-6 items-center justify-center [zoom:1.4] shrink-0 translate-x-2"
                dangerouslySetInnerHTML={{ __html: item.iconHtml }}
              />
            </button>
          );
        })}
      </div>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, dataPoints]);

  const loadResetMapDataWithInitialValues = () => {
    if (APP_CONFIG.DEBUG.MAPS)
      console.log("[loadResetMapDataWithInitialValues]");
    setMapStateTransitionInProgress(true);
    const applicableMapCenter =
      userMapRefreshing && !!userMapActivity?.fleetMap?.center
        ? userMapActivity?.fleetMap?.center
        : APP_CONFIG.MAPS.DEFAULTS.CENTER;
    const applicableMapType =
      userMapRefreshing && !!userMapActivity?.fleetMap?.mapType
        ? userMapActivity?.fleetMap?.mapType
        : (APP_CONFIG.MAPS.TYPES[APP_CONFIG.MAPS.TYPES_INDEX.ROADMAP]
            .type as TMapType);
    const newMapState: Partial<TMapState> = {
      // ...mapState,
      mapCenter: applicableMapCenter,
      mapData: {
        centerPosition: applicableMapCenter,
        ready: true,
      },
      mapType: applicableMapType,
    };
    if (APP_CONFIG.DEBUG.MAPS) console.log("newMapState", newMapState);
    setTimeout(() => {
      dispatch(setMapStateData(newMapState));
      setMapStateTransitionInProgress(false);
    }, 200);
  };

  // set map data on page load
  useEffect(() => {
    if (
      !!allRetrievedVehicles
      // && !isFetchingOrgVehicles
      // && !!allRetrievedVehiclesEvents
      // && !isFetchingOrgVehiclesEvents
    ) {
      // dispatch(setModalsData({ ...modalsState, showVehicleFilter: false })); // disabling this to prevent closing of date filter popup on every refresh
      setDataPoints(
        allRetrievedVehicles
        // ?.map((vehicleItem, _) => ({
        //   ...vehicleItem,
        //   eventData: allRetrievedVehiclesEvents?.find((eventItem) => eventItem.deviceid === vehicleItem.deviceid),
        // }))
      );
      setCheckedVehicles(
        allRetrievedVehicles?.map((item) => getVehicleUUID(item)) ?? []
      );
      loadResetMapDataWithInitialValues();
    }
  }, [
    allRetrievedVehicles /*, isFetchingOrgVehicles*/ /*, allRetrievedVehiclesEvents, isFetchingOrgVehiclesEvents*/,
  ]);

  // search keyword filter
  const [listableDataPoints, setListableDataPoints] = useState<
    TAssetLocationDataPoint[]
  >([]);

  // What actually goes on the map: the user's ticked assets, narrowed by
  // the legend's status filter. Layered rather than written back into
  // checkedVehicles so that clearing the filter restores exactly the
  // ticks the user had, instead of whatever survived it.
  const visibleCheckedVehicles = useMemo(
    () =>
      !statusFilterIsActive
        ? checkedVehicles
        : checkedVehicles.filter((uuid) => {
            const dataPoint = dataPoints.find(
              (item) => getVehicleUUID(item) === uuid
            );
            return !!dataPoint && matchesStatusFilter(dataPoint);
          }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [checkedVehicles, dataPoints, statusFilter, statusFilterIsActive]
  );

  // update map data on checked vehicles change, except when the change is a map refresh
  useEffect(() => {
    if (userMapRefreshing) return;
    mapUpdatesHandler(
      getMapOpsProps(),
      "checkedUpdated",
      visibleCheckedVehicles
    );
    // intentionally removed userMapRefreshing from dependencies
  }, [visibleCheckedVehicles /*, userMapRefreshing*/]); // eslint-disable-line react-hooks/exhaustive-deps

  // carry out map operations on map ready
  const handleMapReady = useCallback(() => {
    if (APP_CONFIG.DEBUG.MAPS)
      console.log("[handleMapReady]", dataOrgGeozones?.results?.length);
    // A refresh takes the map down and builds it again, so a selection made
    // before it has to be put back - and the asset has usually moved since,
    // which is the whole point of the refresh. The restore below places the
    // camera on where it is now, so the initial centring is told to stay
    // out of the way rather than fight it.
    const reselectAssetId = userMapSelection?.fleetMap?.selectedAssetItem;
    const willReselectAsset =
      !!reselectAssetId &&
      mapVehicleHasValidCoords(
        dataPoints.find((item) => getVehicleUUID(item) === reselectAssetId)
      );
    mapOperations(
      {
        ...getMapOpsProps(),
        skipInitialCamera: willReselectAsset,
        onDataPointPushpinClick: (
          dataPoint: TDataPoint | TAssetLocationDataPoint
        ) => {
          if (clearSearchRef.current) {
            clearSearchRef.current(true);
          }
          // Clicking a pin selects that asset - the one already selected
          // included, which re-centres on it and leaves its ring breathing.
          // A second click used to deselect instead, which is where the
          // complaint came from: the ring went down, and since the selection
          // was already that asset the state write that puts it back was a
          // no-op React does not re-render for, so no later click revived it.
          // The comparison also read a `selectedVehicle` captured when the
          // map became ready, so whether a second click deselected at all
          // depended on what was selected the last time the map was rebuilt.
          // Deselecting is what closing the asset details panel does.
          console.log(
            "calling selectMapVehicle with value: (D)",
            getVehicleUUID(dataPoint)
          );
          selectMapVehicle(getVehicleUUID(dataPoint));
        },
        // if this is a refresh, send override param
        ...(!!userMapActivity?.fleetMap?.center &&
        !!userMapActivity?.fleetMap?.zoom
          ? {
              overrideMapView: {
                center: userMapActivity?.fleetMap?.center || null,
                zoom:
                  listableDataPoints.length > 0
                    ? // zoom on last set or use default
                      userMapActivity?.fleetMap?.zoom ||
                      APP_CONFIG.MAPS.DEFAULTS.ZOOM
                    : // use default (show world map)
                      APP_CONFIG.MAPS.DEFAULTS.ZOOM,
                mapType: userMapActivity?.fleetMap?.mapType || null,
              },
            }
          : {}),
      },
      visibleCheckedVehicles
    );
    renderGeozones();

    // if vehicles were filtered via search prior to map refresh, update the map accordingly
    if (userMapRefreshing && !!searchedKeyword) {
      console.log("SKC listableDataPoints", listableDataPoints.length);
      setTimeout(() => {
        mapUpdatesHandler(
          getMapOpsProps(),
          "checkedUpdated",
          listableDataPoints.map((item) => getVehicleUUID(item))
        );
      }, 1000);
    } else if (userMapRefreshing && statusFilterIsActive) {
      // A refresh rebuilds every pin via mapOperations, which drops them all
      // onto the map regardless of the status filter, and the effect that
      // normally hides filtered-out pins ("checkedUpdated") is short-circuited
      // while userMapRefreshing is true. So when the legend status filter is
      // active we must re-apply visibility here, mirroring the search case
      // above, otherwise the filter silently stops applying after every
      // refresh even though its checkboxes still read as set.
      setTimeout(() => {
        mapUpdatesHandler(
          getMapOpsProps(),
          "checkedUpdated",
          visibleCheckedVehicles
        );
      }, 1000);
    }

    // if a vehicle was selected prior to map refresh, re-select it - this
    // re-centres on the asset's current position, puts the highlighted pin
    // back and brings the ring under it back up
    if (willReselectAsset) {
      console.log("re-selecting asset after map refresh:", reselectAssetId);
      setTimeout(() => {
        mapUpdatesHandler(getMapOpsProps(), "focusPushpin", {
          id: reselectAssetId,
          focus: true,
        });
      }, 1000);
    }
  }, [
    mapState,
    dataPoints,
    checkedVehicles,
    // `selectedVehicle` is deliberately not here: the pin click handler above
    // no longer reads it, and reading it from a closure built once, when the
    // map became ready, was the stale comparison this screen used to make.
    mapState?.mapLayerOptions?.hide_geozones,
    dataOrgGeozones, // adding this dependency ensures that updates to geozones are reflected
    userMapActivity,
    userMapSelection,
    userMapRefreshing,
    searchedKeyword,
    listableDataPoints,
    visibleCheckedVehicles,
    statusFilterIsActive,
  ]); // instead of [mapState?.mapData]

  const handleChangeCheckAllVehicles = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (e.target.checked) {
      setCheckedVehicles(dataPoints.map((item) => getVehicleUUID(item)));
    } else {
      setCheckedVehicles([]);
    }
  };

  const handleClickCheckbox = (dataPoint: AssetLocationDetails) => {
    const vehicleUUID = getVehicleUUID(dataPoint);
    if (checkedVehicles.includes(vehicleUUID)) {
      if (APP_CONFIG.DEBUG.MAPS) console.log("uncheck", vehicleUUID);
      setCheckedVehicles(
        checkedVehicles.filter((id: any) => id !== vehicleUUID)
      );
      // hide vehicle details popup if unchecked
      dispatch(
        setModalsData({
          ...modalsState,
          showGroupSelector: false,
          showHierarchyGroupSelector: false,
          showLayerFilter: false,
          showVehicleFilter: false,
          showVehicleDetails: false,
        })
      );
    } else {
      if (APP_CONFIG.DEBUG.MAPS) console.log("check", vehicleUUID);
      setCheckedVehicles([...checkedVehicles, vehicleUUID]);
    }
  };

  const [gImageryMetaApiTrigger, { isFetching: isFetchingGImageryMeta }] =
    useLazyGImageryMetaQuery();

  const handleMapTypeChange = useCallback(
    (mapType: TMapType, latLng?: google.maps.LatLng | null) => {
      if (APP_CONFIG.DEBUG.MAPS)
        console.log(
          "[handleMapTypeChange]",
          mapType,
          "latLng:",
          latLng?.lat(),
          latLng?.lng()
        );

      const successCallback = (gImageryMetaApiResponse?: any) => {
        if (APP_CONFIG.DEBUG.MAPS)
          console.log(
            "successCallback",
            latLng,
            mapRef.current.map.getCenter()
          );
        dispatch(
          // update user activity in store
          setUserMapActivity({
            ...userMapActivity,
            fleetMap: {
              ...userMapActivity.fleetMap,
              mapType: mapType,
            },
          })
        );
        // set map mode using Google Maps JS SDK
        gMapSetMode(
          mapRef.current.map,
          mapType as TMapType,
          latLng || mapRef.current.map.getCenter(),
          {
            gImageryMetaApiResponse,
            // callback checks if streetside mode is enabled and sets applicable map type in store
            onStreetViewVisibleChanged: (panoVisible: boolean) => {
              if (APP_CONFIG.DEBUG.MAPS)
                console.log(
                  "[onStreetViewVisibleChanged] attached at map-fleet.tsx"
                );
              const newMapMode = gIdentifyAndSetMapModeToStore(
                mapRef.current.map
              );
              if (APP_CONFIG.DEBUG.MAPS)
                console.log("SVCHANGED newMapMode:", newMapMode);
              const panorama = mapRef.current.map.getStreetView();
              setTimeout(() => {
                if (!panorama.getVisible()) {
                  // exited streetview
                  const newMapMode = gIdentifyAndSetMapModeToStore(
                    mapRef.current.map
                  );
                  if (APP_CONFIG.DEBUG.MAPS)
                    console.log(
                      "SVCHANGED - pano not visible (exited) newMapMode:",
                      newMapMode
                    );
                  // update user map activity in store
                  // if (!recordActivityAsPage || refreshingRef.current) return;
                  if (userMapRefreshing) {
                    if (APP_CONFIG.DEBUG.MAPS)
                      console.log(
                        "SVCHANGED - userMapRefreshing =",
                        userMapRefreshing,
                        "RETURNING"
                      );
                    return;
                  }
                  if (APP_CONFIG.DEBUG.MAPS)
                    console.log(
                      "SVCHANGED - about to trigger usermapactivity change",
                      newMapMode
                    );
                  // check if streetview exit was not successful, trigger change to roadmap
                  if (newMapMode === "streetview") {
                    if (APP_CONFIG.DEBUG.MAPS)
                      console.log(
                        "SVCHANGED - fix incorrect 'newMapMode' value from 'streetview' to ",
                        GOOGLE_MAP_MODE_NAMES.ROADMAP
                      );
                    gMapSetMode(
                      mapRef.current.map,
                      GOOGLE_MAP_MODE_NAMES.ROADMAP,
                      latLng || mapRef.current.map.getCenter()
                      // { skipEventListenerRegistration: true }
                    );
                  }
                  setTimeout(() => {
                    const newMapMode = gIdentifyAndSetMapModeToStore(
                      mapRef.current.map
                    );
                    if (APP_CONFIG.DEBUG.MAPS)
                      console.log(
                        "SVCHANGED - about to trigger setUserMapActivity change - newMapMode:",
                        newMapMode
                      );
                    dispatch(
                      setUserMapActivity({
                        ...userMapActivity,
                        fleetMap: {
                          // ...activityRef.current?.fleetMap,
                          ...userMapActivity?.fleetMap,
                          center: {
                            latitude: mapRef.current.map.getCenter().lat(),
                            longitude: mapRef.current.map.getCenter().lng(),
                          } as TLatLng,
                          zoom: mapRef.current.map.getZoom(),
                          mapType: newMapMode,
                        },
                      } as TUserMapActivity)
                    );
                  }, 600);
                } else {
                  if (APP_CONFIG.DEBUG.MAPS)
                    console.log("SVCHANGED - pano is visible");
                }
              }, 600);
            },
          }
        );
      };
      // Handle street view
      if (
        APP_CONFIG.MAPS.VIEW_TYPES_REQUIRING_METADATA_CHECK.includes(mapType)
      ) {
        const center: google.maps.LatLng = mapRef.current.map.getCenter();
        if (
          typeof center.lat() !== "number" ||
          typeof center.lng() !== "number"
        ) {
          if (APP_CONFIG.DEBUG.MAPS)
            console.error("Map center coords not found");
          toast.error(
            (APP_CONFIG.DEBUG.ALL ? "[A] " : "") +
              tMain("maps.type_not_supported")
          );
          return;
        }

        gMapEnsureAndLaunchStreetView({
          center,
          successCallback,
        });
      } else {
        successCallback();
      }
    },
    [dispatch, gMapSetMode, gIdentifyAndSetMapModeToStore]
  );

  const handleMapLayerChange = (mapLayerOptions: TMapLayerOptions) => {
    setMapStateTransitionInProgress(true);
    setTimeout(() => {
      dispatch(
        setMapStateData({
          // ...mapState,
          mapLayerOptions,
        })
      );
      setMapStateTransitionInProgress(false);
    }, 100);
  };

  const handleModalCloseClick = () => {
    if (true) return; // added to prevent changing of map view on closing of map type filter modal
    // Reset map to its default state
    mapUpdatesHandler(getMapOpsProps(), "checkedUpdated", checkedVehicles);
  };

  const onGroupSelectionChange = (selectedGroups: string[]) => {
    // if selection is different from filteredGroupData, then reset the filter
    const divergence1 = filteredGroupData
      .filter((group) => group.is_hgroup && group.checked)
      .filter((group) => !selectedGroups.includes(group.groupid));
    const divergence2 = selectedGroups.filter(
      (group) =>
        !filteredGroupData
          .filter((group) => group.is_hgroup && group.checked)
          .map((group) => group.groupid)
          .includes(group)
    );
    if (APP_CONFIG.DEBUG.ALL) console.log("divergence1", divergence1);
    if (APP_CONFIG.DEBUG.ALL) console.log("divergence2", divergence2);
    if (APP_CONFIG.DEBUG.ALL) console.log("selectedGroups", selectedGroups);
    if (divergence1.length > 0 || divergence2.length > 0) {
      handleGroupFilterChange(
        selectedGroups.map((group) => {
          return {
            groupid: group,
            checked: true,
            is_hgroup: true,
          };
        }) as TGroupSelectorItem[] // used `any` to avoid type mismatch as a workaround
      );
    }
  };

  // set selected groups
  useEffect(() => {
    if (accountHasGroupHierarchyEnabled === "1") return;
    if (!!dataOrgGroups && !isFetchingOrgGroups) {
      setSelectedGroups(
        dataOrgGroups?.results?.map((group) => {
          return group.groupid;
        }) ?? ([] as string[])
      );
    }
  }, [dataOrgGroups, isFetchingOrgGroups]);
  // update all groups selected state
  useEffect(() => {
    if (accountHasGroupHierarchyEnabled === "1") return;
    if (!!dataOrgGroups && !isFetchingOrgGroups) {
      setAllGroupsSelected(
        dataOrgGroups?.results?.length === selectedGroups?.length
      );
    }
  }, [selectedGroups, dataOrgGroups, isFetchingOrgGroups]);

  const MemoizedGroupSelector = useMemo(() => {
    return (
      <>
        {accountHasGroupHierarchyEnabled === "1" ? (
          <>
            {modalsState.showHierarchyGroupSelector && (
              <HierarchyGroupSelector
                initiallySelectedGroups={filteredGroupData
                  .filter((group) => group.is_hgroup && group.checked)
                  .map((group) => group.groupid)}
                onSelectionChange={(selectedGroups) => {
                  onGroupSelectionChange(selectedGroups);
                }}
              />
            )}
          </>
        ) : (
          <ModalGroupSelector
            hideGroupAssetToggle={true}
            allGroups={dataOrgGroups?.results ?? []}
            filteredGroupData={filteredGroupData}
            // setFilteredGroupData={(e) => handleGroupFilterChange(e)}
            // 01/28/2025 commented above code and added the below code to fix the issue of API not triggering on group selection change
            // filteredGroupData={
            //   !!dataOrgGroups && !isFetchingOrgGroups
            //     ? (dataOrgGroups?.results?.map((group) => ({
            //         ...group,
            //         is_hgroup: false,
            //         checked: (selectedGroups as string[]).includes(
            //           group.groupid
            //         ),
            //       })) as TGroupSelectorItem[])
            //     : []
            // }
            setFilteredGroupData={(e: TGroupSelectorItem[]) => {
              if (APP_CONFIG.DEBUG.ALL) console.log("setFilteredGroupData", e);
              return setSelectedGroups(
                e
                  ?.filter((group) => group.checked)
                  ?.map((group) => group.groupid)
              );
            }}
            key={`group-selector-${isFetchingOrgGroups.toString()}`}
          />
        )}
      </>
    );
  }, [
    modalsState.showHierarchyGroupSelector,
    accountHasGroupHierarchyEnabled,
    dataOrgGroups,
    filteredGroupData,
  ]);

  useEffect(() => {
    setListableDataPoints(
      dataPoints
        // the legend's status filter, applied within whatever the group
        // filter already narrowed the fetch to
        ?.filter(matchesStatusFilter)
        // filter vehicles based on search keyword
        // this search looks for the keyword in the fields specified in `assetSearchFields` array
        ?.filter((dataPoint) => {
          const keyword = searchedKeyword.trim().toLowerCase();
          if (!keyword) {
            return true;
          }

          const fieldValueByName: Record<string, string | number | undefined> =
            {
              deviceid: dataPoint.deviceid,
              description: dataPoint.description,
              displayname: dataPoint.displayname,
              drivername: dataPoint.drivername,
              driverid: (dataPoint as any).driverid,
              uniqueid: dataPoint.uniqueid,
              vehicleid: dataPoint.vehicleid,
              // serverid: (dataPoint as any).serverid, // value does not exist
              devicecode: (dataPoint as any).devicecode,
              cameraid: (dataPoint as any).cameraid,
              vehiclemake: (dataPoint as any).vehiclemake,
              vehiclemodel: (dataPoint as any).vehiclemodel,
              vehicleyear: (dataPoint as any).vehicleyear,
            };

          return assetSearchFields.some((searchField: string) => {
            const directFieldValue = fieldValueByName[searchField];
            const normalizedValue =
              directFieldValue !== undefined && directFieldValue !== null
                ? String(directFieldValue).toLowerCase()
                : "";

            if (normalizedValue.includes(keyword)) {
              return true;
            }

            return false;
          });
        })
    );
  }, [dataPoints, searchedKeyword, assetSearchFields]);

  // update map data on searched vehicles change
  useEffect(() => {
    if (APP_CONFIG.DEBUG.MAPS) console.log("searched keyword changed");
    mapUpdatesHandler(
      getMapOpsProps(),
      "checkedUpdated",
      listableDataPoints.map((item) => getVehicleUUID(item))
    );
  }, [searchedKeyword, listableDataPoints]);

  const MemoizedRefreshPlayPauseButton = useMemo(() => {
    return (
      <AdminFormFieldSubmit
        type="button"
        variant="primary"
        label={refreshPaused ? t("button_resume") : t("button_pause")}
        onClick={() => setUserRefreshPaused((state) => !state)}
        // disabled={replayInProgress}
        buttonDimensionClass="h-[38px] w-28 px-4"
        // buttonTextClass="text-[10px] uppercase font-semibold"
      />
    );
  }, [refreshPaused]);

  // hide streetview when user clicks anywhere outside map
  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (
        mapState.mapType === GOOGLE_MAP_MODE_NAMES.STREETVIEW &&
        mapWrapperRef.current &&
        !mapWrapperRef.current.contains(event.target)
      ) {
        gMapSetMode(
          mapRef.current.map,
          mapRef.current.map?.lastMapModeBeforeStreetview ||
            GOOGLE_MAP_MODE_NAMES.ROADMAP,
          mapRef.current.map.getCenter()
          // { skipEventListenerRegistration: true }
        );
      }
    };

    if (mapState?.mapType === GOOGLE_MAP_MODE_NAMES.STREETVIEW) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [mapState?.mapType, mapWrapperRef, mapRef]);

  return (
    <>
      {/* Group Filter Popup */}
      {MemoizedGroupSelector}

      <div className={`${APP_CONFIG.DES.DASH.P_HORIZ} py-2`}>
        <div
          className={`flex flex-col md:flex-row rounded-lg ${userIsImpersonated ? "h-[calc(100vh-7rem-40px)]" : "h-[calc(100vh-7rem)]"} overflow-hidden relative`}
        >
          <div
            className={`flex flex-col w-full md:w-72 h-[55vh] md:h-full order-2 md:order-1${
              // hidden xl:block
              !allRetrievedVehicles ? " opacity-40 pointer-events-none" : ""
            } bg-gray-100`}
          >
            <div className="px-4 mt-4">
              <AppSearchBox
                placeholder={t("search_placeholder")}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  debouncedSetSearchKeyword(e.target.value)
                }
                onClear={() => {
                  debouncedSetSearchKeyword("");
                }}
              />
            </div>
            <div className="flex justify-between p-4 items-center">
              <button
                className="flex items-center bg-accent-blue-pale hover:bg-accent-blue-dark hover:text-white group py-2 px-3 rounded-lg gap-2 cursor-pointer"
                type="button"
                onClick={() => {
                  dispatch(
                    setModalsData({
                      ...modalsState,
                      showVehicleDetails: false,
                      showLayerFilter: false,
                      showVehicleFilter: false,
                      ...(accountHasGroupHierarchyEnabled === "1"
                        ? { showHierarchyGroupSelector: true }
                        : { showGroupSelector: true }),
                    })
                  );
                }}
              >
                <p className="font-medium text-lg leading-6">
                  {tAdmin("groups")}
                </p>
                <img
                  src={svgIcons.GroupFilterIcon}
                  className="group-hover:grayscale-0 group-hover:brightness-0 group-hover:invert"
                  alt="group-filter-icon"
                />
              </button>
              <div className="flex gap-2 relative">
                {/* Time Filters Button */}
                <button
                  className={`p-2 hover:bg-accent-blue-pale rounded-lg`}
                  type="button"
                  onClick={() => {
                    dispatch(
                      setModalsData({
                        ...modalsState,
                        showGroupSelector: false,
                        showHierarchyGroupSelector: false,
                        showVehicleDetails: false,
                        showLayerFilter: false,
                        showVehicleFilter: true,
                      })
                    );
                  }}
                >
                  <img
                    src={svgIcons.FilterIcon}
                    alt="filter-icon"
                    className="cursor-pointer"
                  />
                </button>

                {/* Sorting Filter Dropdown Button */}
                <button
                  className={`relative p-2 hover:bg-accent-blue-pale rounded-lg cursor-pointer${showSortingDropdown && " bg-accent-blue-pale z-modal"}`}
                  type="button"
                >
                  <img
                    src={svgIcons.SortIcon}
                    alt="sort-icon"
                    className=""
                    onClick={() => setShowSortingDropdown(!showSortingDropdown)}
                  />
                </button>
                <SortingFilter
                  showSortingDropdown={showSortingDropdown}
                  setShowSortingDropdown={setShowSortingDropdown}
                  sortingOptions={SORTING_OPTIONS}
                  selectedSorting={selectedSorting}
                  setSelectedSorting={(item) => {
                    setSelectedSorting(item);
                    setShowSortingDropdown(false);
                  }}
                />
              </div>
            </div>
            <div className="flex px-4 gap-3">
              <TickCheckbox
                id={`checkAllVehicles`}
                isChecked={
                  dataPoints.length === 0
                    ? false
                    : checkedVehicles.length === dataPoints?.length
                }
                handleChange={handleChangeCheckAllVehicles}
                onClick={(e) => e.stopPropagation()}
                disabled={dataPoints.length === 0}
              />
              <label
                htmlFor={`checkAllVehicles`}
                className="font-semibold text-sm leading-6 text-gray-900 cursor-pointer"
              >
                {tMain("select_all")}
              </label>
            </div>
            {/* <div className="px-3 font-bold text-lg leading-6">
              {t("listing_heading")}
            </div> */}
            <div className="mt-4 flex-grow overflow-auto">
              {listableDataPoints.length > 0 ? (
                <>
                  {[...listableDataPoints]
                    // sort vehicles based on selected sorting option
                    ?.sort((a, b) => {
                      const titleA = a.deviceid; // mapVehicleDisplayTitle(a);
                      const titleB = b.deviceid; // mapVehicleDisplayTitle(b);
                      switch (selectedSorting) {
                        case SORTING_OPTION_VALUES.LATEST_FIRST:
                          return b.eventdata.timestamp - a.eventdata.timestamp;
                        case SORTING_OPTION_VALUES.OLDEST_FIRST:
                          return a.eventdata.timestamp - b.eventdata.timestamp;
                        case SORTING_OPTION_VALUES.ALPHABETICAL_AZ:
                          return titleA?.localeCompare(titleB) ?? 0;
                        case SORTING_OPTION_VALUES.ALPHABETICAL_ZA:
                          return titleB?.localeCompare(titleA) ?? 0;
                        default:
                          return 0;
                      }
                    })
                    // render vehicles to list items
                    ?.map((dataPoint, index: number) => {
                      const vehicleHasValidCoords =
                        mapVehicleHasValidCoords(dataPoint);
                      const vehicleOrientation =
                        mapVehicleOrientation(dataPoint);

                      // show only vehicles for the current page
                      const showVehicleItem =
                        index >=
                          vehicleListCurrentPage *
                            orgVehiclesQueryParams.page_size -
                            orgVehiclesQueryParams.page_size &&
                        index <
                          vehicleListCurrentPage *
                            orgVehiclesQueryParams.page_size;
                      if (!showVehicleItem) return null;

                      return (
                        <MapVehicleListingColumnItem
                          key={index}
                          asideText={
                            <span
                              className={`flex items-center justify-end gap-1 leading-3 text-[10px] ${
                                [
                                  ASSET_MAP_STATUS.DRIVING,
                                  ASSET_MAP_STATUS.IDLE,
                                ].includes(dataPoint?.asset_map_status)
                                  ? "text-field-success"
                                  : dataPoint?.asset_map_status ===
                                      ASSET_MAP_STATUS.IGNITION_OFF
                                    ? "text-accent-orange"
                                    : dataPoint?.asset_map_status ===
                                        ASSET_MAP_STATUS.BATTERY_POWERED
                                      ? "text-[#2A6FDB]"
                                      : "text-field-error-dark"
                              }`}
                            >
                              {t(
                                dataPoint?.asset_map_status ===
                                  ASSET_MAP_STATUS.DRIVING
                                  ? "vehicleStatus.driving"
                                  : dataPoint?.asset_map_status ===
                                      ASSET_MAP_STATUS.IDLE
                                    ? "vehicleStatus.idle_active"
                                    : dataPoint?.asset_map_status ===
                                        ASSET_MAP_STATUS.IGNITION_OFF
                                      ? "vehicleStatus.ignition_off"
                                      : dataPoint?.asset_map_status ===
                                          ASSET_MAP_STATUS.BATTERY_POWERED
                                        ? "vehicleStatus.battery_powered"
                                        : "vehicleStatus.idle_inactive"
                              )}
                              <div
                                dangerouslySetInnerHTML={{
                                  __html:
                                    dataPoint?.asset_map_status ===
                                    ASSET_MAP_STATUS.DRIVING
                                      ? mapVehicleIconWrapped(
                                          "driving",
                                          false,
                                          vehicleOrientation,
                                          undefined, // size (default)
                                          false, // onlyIcon
                                          false // highlightIcon
                                        )
                                      : mapVehicleIconWrapped(
                                          mapVehicleStateIconSlug(dataPoint),
                                          false,
                                          45, // TOOD: need to identify why 45 needs to be provided
                                          undefined, // size (default)
                                          false, // onlyIcon
                                          false // highlightIcon
                                        ),
                                }}
                              />
                            </span>
                          }
                          bottomText={dataPoint.equipmenttype || "-"} // dataPoint.licenseplate || dataPoint.vehicleid} // dataPoint.equipmenttype
                          checked={checkedVehicles.includes(
                            getVehicleUUID(dataPoint)
                          )}
                          description={mapVehicleMakeModel(dataPoint)} // dataPoint.displayname || dataPoint.vehicleid} // dataPoint.equipmenttype
                          id={`checkedVehicle-${getVehicleUUID(dataPoint)}`}
                          onClickCheckbox={() => handleClickCheckbox(dataPoint)}
                          onClick={() => {
                            if (clearSearchRef.current) {
                              clearSearchRef.current(true);
                            }
                            if (
                              !checkedVehicles.includes(
                                getVehicleUUID(dataPoint)
                              )
                            ) {
                              // select the vehicle if not already selected
                              setCheckedVehicles([
                                ...checkedVehicles,
                                getVehicleUUID(dataPoint),
                              ]);
                            }
                            console.log(
                              "calling selectMapVehicle with value: (E)",
                              getVehicleUUID(dataPoint)
                            );
                            selectMapVehicle(getVehicleUUID(dataPoint));
                          }}
                          title={consistentDisplayAssetItem(
                            dataPoint.deviceid,
                            dataPoint.description,
                            dataPoint.displayname,
                            dataPoint.drivername,
                            showDriver
                          )} // title={`${dataPoint.deviceid}${dataPoint.description ? ` | ${dataPoint.description}` : ""}${dataPoint.displayname ? ` | ${dataPoint.displayname}` : ""}`} // mapVehicleDisplayTitle(dataPoint)
                          selectable={vehicleHasValidCoords}
                          selected={
                            selectedVehicle === getVehicleUUID(dataPoint)
                          }
                        />
                      );
                    })}
                </>
              ) : (
                <div className="flex items-center justify-center h-full relative">
                  {isFetchingAllAssetLocations ||
                  isFetchingOrgGroups /* || isFetchingOrgVehiclesEvents*/ ||
                  !mapState?.mapScriptLoaded /* || !mapState?.pageMapLoaded*/ ? (
                    <>
                      <MapLoadingAnimation
                        bgClassName=""
                        imageClassName="w-12 h-12 mb-12"
                      />
                    </>
                  ) : (
                    <p className="text-gray-400 text-base font-semibold">
                      {t("no_vehicles_found")}
                    </p>
                  )}
                </div>
              )}
            </div>
            {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
              <pre className="absolute opacity-50 bottom-0 left-0 text-xs">
                {vehicleListCurrentPage * orgVehiclesQueryParams.page_size -
                  orgVehiclesQueryParams.page_size +
                  1}
                -{vehicleListCurrentPage * orgVehiclesQueryParams.page_size}
              </pre>
            )}
            {!!listableDataPoints &&
              listableDataPoints?.length >
                APP_CONFIG.LISTINGS.VEHICLE_PAGE_SIZE && (
                <Pagination
                  pageSize={orgVehiclesQueryParams.page_size}
                  pageRangeDisplayed={1}
                  marginPagesDisplayed={1}
                  handlePageSizeChange={(e: TSelectboxOption | null) => {
                    // dispatch(setListingQueryParams({ ...listingQueryParams, users: { ...orgRolesQueryParams, page: 1, page_size: parseInt(`${e?.value}`) }}));
                  }}
                  totalPages={
                    listableDataPoints?.length
                      ? Math.ceil(
                          listableDataPoints?.length /
                            orgVehiclesQueryParams.page_size
                        )
                      : 1
                  }
                  // forcePage={orgVehiclesQueryParams.page - 1}
                  forcePage={vehicleListCurrentPage - 1}
                  handlePageClick={(data: TPaginationSelected) => {
                    console.log("data", data);
                    setVehicleListCurrentPage(data.selected + 1);
                    // dispatch(setListingQueryParams({ ...listingQueryParams, users: { ...orgRolesQueryParams, page: data?.selected + 1 }}));
                  }}
                  paginCurrentPage={orgVehiclesQueryParams.page}
                  paginItemsOnCurrentPage={allRetrievedVehicles?.length || 0}
                  paginTotalItemsCount={allRetrievedVehicles?.length}
                  onlyPageChange={true}
                  size={1}
                  wrapperClass="flex py-3 items-center"
                />
              )}
          </div>
          <div
            className="h-[45vh] md:h-full md:flex-grow relative map-fleet-map-wrapper order-1 md:order-2"
            ref={mapWrapperRef}
          >
            {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
              <div className="absolute top-0 left-0 px-2 bg-[#FFFFFFAA] text-field-error-dark text-[10px] font-bold uppercase z-10">
                mapType: {mapState?.mapType}
              </div>
            )}
            {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
              <>
                <pre className="absolute bottom-4 left-5 px-2 bg-[#FFFFFFAA] p-2 mt-1 text-[10px] max-h-[calc(100vh-24rem)] overflow-auto Xpointer-events-none z-map-popup">
                  allRetrievedVehicles.results (count):{" "}
                  {allRetrievedVehicles?.length}
                  <br />
                  isFetchingOrgGeozones:{" "}
                  <span
                    className={
                      isFetchingOrgGeozones === true
                        ? "text-field-error-dark"
                        : ""
                    }
                  >
                    {JSON.stringify(isFetchingOrgGeozones)}
                  </span>
                  <br />
                  isFetchingAllAssetLocations:{" "}
                  <span
                    className={
                      isFetchingAllAssetLocations === true
                        ? "text-field-error-dark"
                        : ""
                    }
                  >
                    {JSON.stringify(isFetchingAllAssetLocations)}
                  </span>
                  <br />
                  {/* isFetchingOrgVehiclesEvents: <span className={isFetchingOrgVehiclesEvents === true ? 'text-field-error-dark' : ''}>{JSON.stringify(isFetchingOrgVehiclesEvents)}</span><br /> */}
                  mapState.mapScriptLoaded:{" "}
                  <span
                    className={
                      mapState?.mapScriptLoaded !== true
                        ? "text-field-error-dark"
                        : ""
                    }
                  >
                    {JSON.stringify(mapState?.mapScriptLoaded)}
                  </span>
                  <br />
                  mapState.pageMapLoaded:{" "}
                  <span
                    className={
                      mapState?.pageMapLoaded !== true
                        ? "text-field-error-dark"
                        : ""
                    }
                  >
                    {JSON.stringify(mapState?.pageMapLoaded)}
                  </span>{" "}
                  (ignored)
                  <br />
                  mapState.mapData.ready:{" "}
                  <span
                    className={
                      mapState?.mapData?.ready !== true
                        ? "text-field-error-dark"
                        : ""
                    }
                  >
                    {JSON.stringify(mapState?.mapData?.ready)}
                  </span>
                  <br />
                  ---------------------------------
                  <br />
                  mapState?.mapType:{" "}
                  {JSON.stringify(mapState?.mapType, null, 2)}
                  <br />
                  ---------------------------------
                  <br />
                  mapState?.mapCenter:{" "}
                  {JSON.stringify(mapState?.mapCenter, null, 2)}
                  <br />
                  ---------------------------------
                  <br />
                  userMapActivity: {JSON.stringify(userMapActivity, null, 2)}
                  <br />
                  ---------------------------------
                  <br />
                  userMapRefreshing:{" "}
                  {JSON.stringify(userMapRefreshing, null, 2)}
                  <br />
                  ---------------------------------
                  <br />
                  userMapSelection: {JSON.stringify(userMapSelection, null, 2)}
                  <br />
                  ---------------------------------
                </pre>
                <pre className="absolute bottom-4 left-72 px-2 bg-[#FFFFFFAA] p-2 mt-1 text-[10px] max-h-[calc(100vh-24rem)] overflow-auto Xpointer-events-none z-map-popup">
                  FETCHING MAP DATA:
                  <br />
                  mapStateTransitionInProgress:{" "}
                  {JSON.stringify(mapStateTransitionInProgress)}
                  <br />
                  vehiclesBatch.current: {vehiclesBatch.current}
                  <br />
                  ---------------------------------
                  <br />
                  selectedVehicle: {selectedVehicle}
                  <br />
                  checkedVehicles (count): {checkedVehicles.length}
                  <br />
                  vehicleListCurrentPage: {vehicleListCurrentPage}
                  <br />
                  ---------------------------------
                  <br />
                  eventDataFilters:
                  <br />
                  {JSON.stringify(eventDataFilters, null, 2)}
                  <br />
                  ---------------------------------
                  <br />
                  showAddressSearchbox: {JSON.stringify(showAddressSearchbox)}
                  <br />
                  ---------------------------------
                  <br />
                  filteredGroupData count:{" "}
                  {JSON.stringify(
                    filteredGroupData.filter((item) => !!item.checked).length,
                    null,
                    2
                  )}
                  <br />
                  filteredGroupData: <br />
                  <span className="block h-36 overflow-auto">
                    {JSON.stringify(filteredGroupData, null, 2)}
                  </span>
                </pre>
              </>
            )}

            {/* Moved MapTopRightControls out of the render conditions (below) to help retain searched location in searchbox */}
            {
              <MapTopRightControls
                mapRef={mapRef}
                onGAutocompleteClearCallback={() => {
                  // reset map to default view
                  mapUpdatesHandler(
                    getMapOpsProps(),
                    "centerToPushpin",
                    checkedVehicles
                  );
                }}
                onSearchboxToggle={setMapSearchboxOpen}
                onDirectionChange={handleDirectionsChange}
                clearSearchRef={clearSearchRef}
              />
            }

            {!isFetchingOrgGeozones &&
            !!allRetrievedVehicles?.length &&
            !!dataAllAssetLocations?.length &&
            allRetrievedVehicles.length >= allRetrievedVehicles?.length && // load map only when all vehicles are loaded (prevent repeated loading / blinking of map)
            /* && !isFetchingOrgVehiclesEvents*/
            !mapStateTransitionInProgress ? (
              <>
                <BasicMap
                  className={`bg-gray-200 h-full w-full${mapSearchboxOpen ? " map-searchbox-open" : ""}`}
                  mapRef={mapRef}
                  mapData={mapState?.mapData}
                  recordActivityAsPage="fleetMap"
                  // setMapData={() => {}}
                  nativeMapControls
                  onMapReady={handleMapReady}
                  enableWeather
                />

                {/* Extra loader to be shown while map refreshes */}
                {hideMapNearRefresh && (
                  <MapLoadingAnimation
                    // bgClassName="bg-red-200"
                    bgOpacityClassName="bg-opacity-100"
                  />
                )}

                <DirectionsPanel
                  directionsRenderer={directionsRenderer}
                  onClose={() => setDirectionsRenderer(null)}
                />

                {/* Refresh Control Buttons */}
                {!!mapState?.mapType &&
                  // instead of hiding completely for birdseye and streetview, we will show refresh control but adjust the placement as appropriate
                  // !APP_CONFIG.MAPS.SPECIAL_VIEW_TYPES.includes(
                  //   mapState?.mapType
                  // ) &&
                  !(mobileHelpers.viewportIsMobile && mapSearchboxOpen) && (
                    <div
                      className={`absolute ${mapState?.mapType === GOOGLE_MAP_MODE_NAMES.STREETVIEW ? "top-20" : "top-6 mt-1"} ${APP_CONFIG.DEBUG.DISPLAY_VERBOSE && false ? "left-72 ms-2" : "left-5"} flex flex-col gap-2 z-[${APP_CONFIG.MAPS.REFRESH_BUTTONS_ZINDEX}]`}
                    >
                      {/* Refresh Control Box */}
                      <div className="relative bg-white shadow-md rounded-lg px-3 pt-3 pb-2 flex flex-col gap-3 overflow-hidden">
                        <div
                          className={`flex flex-col gap-3${mapSearchboxOpen ? " opacity-60" : ""}`}
                        >
                          {refreshOptionsExpanded && (
                            <h4 className="flex items-center justify-between text-accent-blue-dark font-semibold">
                              {t("live_updates")}
                              <button
                                type="button"
                                className="size-7 flex items-center justify-center rounded-lg hover:bg-gray-200 focus:bg-gray-200"
                                onClick={() => setRefreshOptionsExpanded(false)}
                              >
                                <img
                                  src={svgIcons.MinimizeIcon}
                                  alt="minimize"
                                />
                              </button>
                            </h4>
                          )}
                          <div className="flex items-center justify-between Xgap-2">
                            <div
                              className={`min-w-20 h-7 flex items-center gap-2 text-base me-2`}
                            >
                              <img
                                src={
                                  refreshPaused
                                    ? svgIcons.UpdatesPaused
                                    : svgIcons.UpdatesLive
                                }
                                alt={refreshPaused ? "paused" : "live"}
                                className="flex"
                              />
                              {refreshPaused
                                ? t("updates_paused_status")
                                : t("updates_live_status")}
                            </div>
                            {refreshOptionsExpanded ? (
                              <>{MemoizedRefreshPlayPauseButton}</>
                            ) : (
                              <>
                                {!mapSearchboxOpen && (
                                  <button
                                    type="button"
                                    className="size-7 flex items-center justify-center rounded-lg hover:bg-gray-200 focus:bg-gray-200"
                                    onClick={() =>
                                      setRefreshOptionsExpanded(
                                        (state) => !state
                                      )
                                    }
                                  >
                                    <img
                                      src={svgIcons.ExpandWindowIcon}
                                      alt="expand"
                                    />
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                        {/* Refresh countdown progress bar */}
                        <RefreshCountdownBar
                          nextRefreshAtRef={nextRefreshAtRef}
                          refreshTotalMsRef={refreshTotalMsRef}
                          refreshPaused={refreshPaused}
                          userMapRefreshing={userMapRefreshing}
                          translationFn={t}
                          refreshFrequency={refreshFrequency}
                          expanded={refreshOptionsExpanded}
                        />
                        {refreshOptionsExpanded && (
                          <>
                            <hr className="h-1" />
                            <div className="flex items-center gap-2 -mt-2 pb-1">
                              <div className="text-base">{t("frequency")}</div>
                              <AdminFormFieldDropdown
                                label={false}
                                id="frequency"
                                name="frequency"
                                value={refreshFrequency}
                                options={
                                  APP_CONFIG.MAPS.REFRESH_FREQUENCY_OPTIONS
                                }
                                onChange={(e) => {
                                  setRefreshFrequency(e?.value ?? "30");
                                }}
                                customWrapperClass="w-28"
                                customSelectboxClass=""
                                dimensionClass="w-full h-7"
                                menuPlacement={
                                  APP_CONFIG.DEBUG.ALL ? "bottom" : "top"
                                }
                                disabled={refreshPaused} // replayInProgress ||
                              />
                            </div>
                          </>
                        )}
                      </div>

                      {/* Legend box - also the status filter */}
                      <div
                        className={`bg-white shadow-md rounded-lg px-3 pt-3 mt-2 ${
                          legendCollapsed ? "pb-3 w-auto" : "pb-2 w-48"
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => setLegendCollapsed((state) => !state)}
                          aria-expanded={!legendCollapsed}
                          className="w-full flex items-center justify-between gap-2 text-accent-blue-dark font-semibold text-sm cursor-pointer"
                        >
                          <span className="whitespace-nowrap">
                            Legend / Filter
                            {statusFilterIsActive && !legendCollapsed && (
                              <span className="text-gray-400 font-normal">
                                {" "}
                                ({statusFilter.length}/
                                {ALL_MAP_STATUSES.length})
                              </span>
                            )}
                          </span>
                          <span className="text-gray-400 leading-none shrink-0">
                            {legendCollapsed ? "+" : "−"}
                          </span>
                        </button>
                        {!legendCollapsed && (
                          <>
                            <hr className="my-2" />
                            {MemoizedLegendBox}
                            {statusFilterIsActive && (
                              <button
                                type="button"
                                onClick={() =>
                                  setStatusFilter(ALL_MAP_STATUSES)
                                }
                                className="w-full text-center text-xs font-medium text-accent-blue-bright hover:underline pt-1 cursor-pointer"
                              >
                                Show all statuses
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  )}
              </>
            ) : (
              <div className="h-full w-full flex items-center justify-center relative">
                {typeof allRetrievedVehicles?.length !== "undefined" &&
                allRetrievedVehicles?.length > 0 ? (
                  <MapLoadingAnimation
                    // bgClassName="bg-red-200"
                    bgOpacityClassName="bg-opacity-100"
                  />
                ) : (
                  <p className="absolute w-full h-full flex items-center justify-center bg-gray-50 text-gray-400 text-lg font-bold">
                    {!mapState?.mapScriptLoaded || isFetchingAllAssetLocations
                      ? tMain("maps.receiving_data")
                      : t("no_vehicles_found")}
                  </p>
                )}
                {APP_CONFIG.DEBUG.MAPS && (
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 mt-8">
                    <h4 className="font-bold text-lg text-black">
                      {!mapState?.mapData ||
                      Object.keys(mapState?.mapData).length < 2
                        ? tMain("maps.receiving_data")
                        : ""}
                    </h4>
                  </div>
                )}
              </div>
            )}
            {mapState?.mapType !== GOOGLE_MAP_MODE_NAMES.STREETVIEW && (
              <ModalVehicleDetails
                eventTimestamp={eventDataFilters.calculatedUnixTimestamp}
                handleMapTypeChange={handleMapTypeChange}
                vehicleId={selectedVehicle}
                vehicleData={dataPoints.find(
                  (vehicle) => getVehicleUUID(vehicle) === selectedVehicle
                )}
                onDetailModalClose={handleModalCloseClick}
              />
            )}
            <ModalClusterVehiclesList
              visible={clusterVehiclesModal.visible}
              count={clusterVehiclesModal.count}
              entityLabel={clusterVehiclesModal.entityLabel}
              markers={clusterVehiclesModal.markers}
              onHide={closeClusterVehiclesModal}
              onVehicleClick={handleClusterVehicleDetailsOpen}
            />
            <ModalLayerFilters
              onMapTypeChange={(mapType) =>
                handleMapTypeChange(
                  mapType,
                  !!userMapActivity.fleetMap.center?.latitude &&
                    !!userMapActivity.fleetMap.center?.longitude
                    ? new google.maps.LatLng(
                        userMapActivity.fleetMap.center.latitude,
                        userMapActivity.fleetMap.center.longitude
                      )
                    : undefined
                )
              }
              onMapLayerChange={handleMapLayerChange}
              onMapCloseClick={handleModalCloseClick}
              weatherOptionEnabled
            />
          </div>
        </div>
      </div>

      <ModalMapFleetFilters
        loadingData={
          !allRetrievedVehicles &&
          isFetchingAllAssetLocations /* || isFetchingOrgVehiclesEvents*/
        }
        savedFilterValues={eventDataFilters}
        onFilterChange={(filterData: MapFleetFiltersData) => {
          // dispatch(setModalsData({ ...modalsState, showVehicleFilter: false }));
          if (
            filterData.date_time === eventDataFilters.date_time &&
            filterData.timezone === eventDataFilters.timezone
          ) {
            dispatch(
              setModalsData({ ...modalsState, showVehicleFilter: false })
            );
            return;
          }
          let calculatedUnixTimestamp =
            eventDataFilters.calculatedUnixTimestamp;
          if (!!filterData.date_time && !!filterData.timezone) {
            const selectedDate = new Date(filterData.date_time); // `${filterData.date} ${filterData.time}:00`
            const utcDate = fromZonedTime(
              selectedDate,
              convertToTZForDateFns(filterData.timezone)
            );
            const jsTime = new Date(utcDate).getTime();
            if (!!jsTime && !isNaN(jsTime)) {
              calculatedUnixTimestamp = jsTime / 1000;
              if (APP_CONFIG.DEBUG.ALL)
                console.log("unixTimestamp", calculatedUnixTimestamp);
            } else {
              if (APP_CONFIG.DEBUG.ALL)
                console.error("Invalid datetime", jsTime);
            }
          }
          const newFilterData = { ...filterData, calculatedUnixTimestamp };
          if (APP_CONFIG.DEBUG.ALL) console.log("newFilterData", newFilterData);
          setEventDataFilters(newFilterData);
          setDateFiltersUpdated(true);
          // close the modal
          setTimeout(() => {
            dispatch(
              setModalsData({ ...modalsState, showVehicleFilter: false })
            );
          }, 200);
        }}
      />
      {thisOrgCameraRecorderType ===
      ACCOUNT_RECORDER_TYPE_VALUES.VISIONMAX ? null : (
        <ModalVehicleLiveCameras />
      )}

      {/* IT APPEARS THIS MODAL IS NOT IN USE <ModalMapFilter /> */}
    </>
  );
};

export default ScreenMapFleet;
